import React, { useState, useEffect, useCallback } from "react";

// --- START: CORRECTED IMPORTS ---
import execImg from "../../assets/usecase/executive_bw.png";
import salesImg from "../../assets/usecase/sales_bw.png";
import hrImg from "../../assets/usecase/hr_bw.png";
import manufImg from "../../assets/usecase/manufacturing_bw.png";
import marketingImg from "../../assets/usecase/marketing_bw.png";
import ITImg from "../../assets/usecase/IT_bw.png";
import ITRmg from "../../assets/usecase/IR_bw.png"; // IR Image is now used!
// --- END: CORRECTED IMPORTS ---

// ------------------------------------
// Default slides (your use cases)
// ------------------------------------
const defaultSlides = [
  {
    id: "01",
    area: "Executive Officers",
    role: "Control & decision making",
    statement: "Too small and outside the scope of regulatory reporting.",
    image: execImg,
    exposures: [
      { label: "Banks", value: "Credit lines, covenants, funding risk" },
      { label: "Insurers", value: "D&O coverage, exclusions, limits" },
      { label: "Suppliers", value: "Dependency on key vendors & contracts" },
      { label: "Clients", value: "Key account concentration & churn risk" },
    ],
    outcomes: [
      "💸 Regulatory fines & penalties",
      "⚖️ Personal liability for directors",
      "📰 Reputation damage & loss of trust",
    ],
  },
  {
    id: "02",
    area: "Sales",
    role: "Revenue generation & client acquisition",
    statement: "Targets, incentives and pressure can drive hidden risk.",
    image: salesImg,
    exposures: [
      { label: "Mis-selling", value: "Over-promising features & returns" },
      { label: "Incentives", value: "Bonus schemes that distort behaviour" },
      {
        label: "Client suitability",
        value: "Selling complex products to the wrong segments",
      },
      {
        label: "Territories",
        value: "Cross-border rules & local marketing restrictions",
      },
    ],
    outcomes: [
      "💥 Complaints & disputes",
      "💸 Chargebacks, refunds, clawbacks",
      "⚖️ Mis-selling investigations & class actions",
    ],
  },
  {
    id: "03",
    area: "Marketing",
    role: "Brand, awareness & growth",
    statement:
      "AI agents lower cost per campaign, but increase content, IP and ethics risk.",
    image: marketingImg,
    exposures: [
      {
        label: "AI agents",
        value: "Synthetic copy, images & targeting at massive scale",
      },
      {
        label: "Brand safety",
        value: "Ads next to harmful or offensive content",
      },
      {
        label: "Data use",
        value: "Tracking, cookies & profiling individuals",
      },
      {
        label: "Third parties",
        value: "Agencies, influencers & opaque media buying",
      },
    ],
    outcomes: [
      "🤯 Viral backlash & cancel campaigns",
      "🧾 Unproductive spend & budget overruns",
      "⚖️ IP, copyright & advertising standards challenges",
    ],
  },
  {
    id: "04",
    area: "HR",
    role: "People, culture & employment lifecycle",
    statement:
      "Hiring, performance management and exits all create concentrated people-risk.",
    image: hrImg,
    exposures: [
      {
        label: "Hiring",
        value: "Bias, unfair selection, poor background checks",
      },
      {
        label: "Conduct",
        value: "Bullying, harassment and whistleblowing cases",
      },
      {
        label: "Contracts",
        value: "Misclassification, unpaid overtime, weak clauses",
      },
      {
        label: "Data",
        value: "Employee health, pay and performance data misuse",
      },
    ],
    outcomes: [
      "⚖️ Employment tribunals & settlements",
      "💸 Higher turnover & hiring costs",
      "😞 Culture damage & loss of key talent",
    ],
  },
  {
    id: "05",
    area: "IT",
    role: "Infrastructure, systems & security",
    statement: "Everything works… until it doesn’t.",
    image: ITImg,
    exposures: [
      {
        label: "Cyber",
        value: "Ransomware, phishing, credential stuffing",
      },
      {
        label: "Availability",
        value: "Single points of failure & fragile integrations",
      },
      {
        label: "Shadow IT",
        value: "Unapproved SaaS and personal devices",
      },
      {
        label: "Legacy",
        value: "Obsolete systems with no support or patching",
      },
    ],
    outcomes: [
      "🛑 Outages & downtime",
      "📉 Lost transactions & customer churn",
      "🚨 Breach notifications, forensic costs & fines",
    ],
  },
  {
    id: "06",
    area: "Manufacturing",
    role: "Physical production & fulfilment",
    statement:
      "Small process errors can scale into systemic quality and safety issues.",
    image: manufImg,
    exposures: [
      {
        label: "Supply chain",
        value: "Single-source suppliers & logistics bottlenecks",
      },
      {
        label: "Quality",
        value: "Defects, contamination, missing controls",
      },
      {
        label: "Safety",
        value: "Worker injury, unsafe equipment, weak PPE culture",
      },
      {
        label: "Environment",
        value: "Spills, emissions, and waste handling failures",
      },
    ],
    outcomes: [
      "📦 Product recalls & scrap",
      "⚠️ Injuries & regulatory investigations",
      "🌍 Environmental penalties & community pushback",
    ],
  },
  {
    // CORRECTED SLIDE 07: INVESTOR RELATIONS
    id: "07",
    area: "Investor Relations (IR)",
    role: "Investor confidence, capital attraction & valuation",
    statement: "ESG is no longer a side project—it’s a material factor driving investment decisions and company valuation.",
    image: ITRmg, // **Using the imported ITRmg**
    exposures: [
      { label: "Shareholder Activism", value: "Proxy votes, divestment campaigns & pressure on governance" },
      { label: "Capital Access", value: "Being excluded from ESG funds, higher cost of capital (loans)" },
      { label: "Rating Agencies", value: "Downgrades from agencies like MSCI, Sustainalytics, hurting appeal" },
      { label: "Greenwashing Risk", value: "Disclosures that are found to be misleading or unsubstantiated" },
    ],
    outcomes: [
      "💰 Loss of Investment: Exclusion from major ESG/Sustainability-focused funds",
      "📉 Share Price Impact: Negative news or rating downgrade causes market slump",
      "📢 Investor Lawsuits: Legal action over failure to disclose material ESG risks",
    ],
  },
];

// ------------------------------------
// Component
// ------------------------------------
// zoomFactor        → how wide the active slide is on desktop
// mobileZoomFactor  → how wide the active slide is on mobile
const UseCase = ({
  slides = defaultSlides,
  zoomFactor = 8.5,
  mobileZoomFactor = 2.5,
}) => {
  const [activeIndex, setActiveIndex] = useState(-1);

  const setActiveSlide = (index) => {
    setActiveIndex((prev) => (prev === index ? -1 : index));
  };

  const nextSlide = useCallback(() => {
    const next = activeIndex === -1 ? 0 : (activeIndex + 1) % slides.length;
    setActiveIndex(next);
  }, [activeIndex, slides.length]);

  const previousSlide = useCallback(() => {
    const prev =
      activeIndex === -1
        ? slides.length - 1
        : (activeIndex - 1 + slides.length) % slides.length;
    setActiveIndex(prev);
  }, [activeIndex, slides.length]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "ArrowLeft") previousSlide();
      if (e.key === "ArrowRight") nextSlide();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [nextSlide, previousSlide]);

  return (
    <>
      {/* INLINE CSS – Final version including mobile arrow hide rule */}
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .slider-container {
          width: 100%;
          max-width: 1200px;
          height: 800px;  /* 🔒 Fixed height – zoom only affects horizontal width */
          position: relative;
          overflow: hidden;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
          margin: 0 auto;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          background: #f0f2f5;
        }

        .now-showing {
          position: absolute;
          top: 20px;
          left: 20px;
          color: #9fff6b;
          font-size: 14px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
          z-index: 10;
        }

        .now-showing::before {
          content: "";
          width: 6px;
          height: 6px;
          background: #9fff6b;
          border-radius: 50%;
        }

        .accordion-slider {
          display: flex;       /* ← horizontal flex layout */
          height: 100%;
          position: relative;
        }

        .slide {
          flex: 1;             /* ← base width for each slide */
          position: relative;
          cursor: pointer;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
          filter: grayscale(1);
        }

        .slide:hover {
          filter: grayscale(0);
        }

        /* 🔧 HORIZONTAL ZOOM CONTROL (DESKTOP) */
        .slide.active {
          flex: ${zoomFactor};
          filter: grayscale(0);
        }

        .slide::before {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.8) 80%);
        }

        .slide-content {
          position: absolute;
          bottom: 30px;
          left: 30px;
          right: 30px;
          color: white;
          z-index: 2;
        }

        .slide.active .slide-content {
          bottom: 80px;
          transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.2s;
        }

        .slide-number {
          font-size: 64px;
          font-weight: 300;
          color: rgba(255, 255, 255, 0.6);
          line-height: 1;
          position: absolute;
          bottom: 30px;
          left: 30px;
          transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .slide.active .slide-number {
          bottom: auto;
          top: -50px;
          font-size: 48px;
          left: 0;
        }

        .car-brand {
          font-size: 16px;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: 5px;
          transform: rotate(-90deg);
          transform-origin: left bottom;
          position: absolute;
          bottom: 100px;
          left: 30px;
          white-space: nowrap;
          transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .slide.active .car-brand {
          transform: rotate(0deg);
          position: static;
          transform-origin: unset;
        }

        .car-name {
          font-size: 28px;
          font-weight: 700;
          margin-bottom: 8px;
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          transition-delay: 0s;
        }

        .slide.active .car-name {
          opacity: 1;
          transform: translateY(0);
          transition-delay: 0.3s;
        }

        .car-subtitle {
          font-size: 16px;
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: 20px;
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          transition-delay: 0s;
        }

        .slide.active .car-subtitle {
          opacity: 1;
          transform: translateY(0);
          transition-delay: 0.4s;
        }

        .car-specs {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          transition-delay: 0s;
        }

        .slide.active .car-specs {
          opacity: 1;
          transform: translateY(0);
          transition-delay: 0.5s;
        }

        .spec-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 6px;
          font-size: 14px;
          opacity: 0;
          transform: translateX(-20px);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .slide.active .spec-row {
          opacity: 1;
          transform: translateX(0);
        }

        .spec-label {
          color: rgba(255, 255, 255, 0.7);
        }

        .spec-value {
          color: white;
          font-weight: 600;
          margin-left: 8px;
        }

        .performance-badges {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 15px;
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          transition-delay: 0s;
        }

        .slide.active .performance-badges {
          opacity: 1;
          transform: translateY(0);
          transition-delay: 0.8s;
        }

        .badge {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 8px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          opacity: 0;
          transform: scale(0.8);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .slide.active .badge {
          opacity: 1;
          transform: scale(1);
        }

        .badge-icon {
          width: 8px;
          height: 8px;
          background: #9fff6b;
          border-radius: 50%;
        }

        .navigation-arrows {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 50px;
          height: 50px;
          background: rgba(0, 0, 0, 0.3);
          border: none;
          border-radius: 50%;
          color: white;
          cursor: pointer;
          font-size: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          z-index: 4;
          backdrop-filter: blur(10px);
        }

        .nav-prev {
          left: 20px;
        }

        .nav-next {
          right: 20px;
        }

        .navigation-arrows:hover {
          background: rgba(0, 0, 0, 0.5);
        }

        @media (max-width: 768px) {
          .accordion-slider {
            flex-direction: column;
          }

          .slide {
            flex: 1;
            min-height: 80px;
          }

          /* 🔧 MOBILE ZOOM CONTROL (VERTICAL) */
          .slide.active {
            flex: ${mobileZoomFactor};
          }

          /* 🔒 HIDE ARROWS ON MOBILE */
          .navigation-arrows {
             display: none;
          }

          .slide-number {
            font-size: 32px;
          }

          .car-brand {
            transform: none;
            position: static;
          }
        }
      `}</style>

      <div className="slider-container">
        <div className="now-showing">Use Case Explorer</div>

        <div className="accordion-slider">
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className={`slide ${activeIndex === index ? "active" : ""}`}
              style={{ backgroundImage: `url(${slide.image})` }}
              onClick={() => setActiveSlide(index)}
            >
              <div className="slide-content">
                <div className="slide-number">{slide.id}</div>
                <div className="car-brand">{slide.area}</div>
                <div className="car-name">{slide.role}</div>
                <div className="car-subtitle">{slide.statement}</div>

                <div className="car-specs">
                  {slide.exposures.map((e, i) => (
                    <div className="spec-row" key={i}>
                      <span className="spec-label">{e.label}</span>
                      <span className="spec-value">{e.value}</span>
                    </div>
                  ))}
                </div>

                <div className="performance-badges">
                  {slide.outcomes.map((outcome, i) => (
                    <div className="badge" key={i}>
                      <div className="badge-icon"></div>
                      <span>{outcome}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <button className="navigation-arrows nav-prev" onClick={previousSlide}>
          ‹
        </button>

        <button className="navigation-arrows nav-next" onClick={nextSlide}>
          ›
        </button>
      </div>
    </>
  );
};

export default UseCase;